import { type CSSProperties, type ReactNode, useEffect, useRef } from "react";
import { useIsSmartEdit } from "~/app/config/pageMetadataContext";
import { useTabHashLogic } from "~/features/search/hooks/useTabHashLogic";
import { trackPlpTabActivation } from "~/app/analytics/ga/ga";
import { hasAnyContent } from "~/common/utils/domUtils";
import { useTranslation } from "react-i18next";
import { useServerSideContent } from "~/common/hooks/useServerSideContent";
import ProductResults from "~/features/search/components/ProductResults/ProductResults";
import { useDispatch, useSelector } from "react-redux";
import { useOnMount } from "~/common/hooks/useOnMount";
import { searchResultsMounted } from "~/features/search/redux/actions";
import {
  selectCurrentPage,
  selectIsInFlight,
  selectIsInit,
  selectProductResultAmount,
  selectTechnicalInfo,
} from "~/features/search/redux/searchSelectors";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { SearchPageTypeProvider } from "~/features/search/context/SearchPageTypeContext";
import { ActiveSalesCampaignCodesProvider } from "~/features/salesCampaign/hooks/useActiveSalesCampaignCodes";
import { RecentlyViewedProducts } from "~/features/RecentlyViewedProducts/RecentlyViewedProducts";
import { getSearchResultRecommendation } from "~/features/saleRecommendations/searchResultRecommendations";
import { ChildCategoryNavigation } from "~/features/categoryNavigation/components/ChildCategoryNavigation";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import { Tab } from "~/common/mui/Tabs/Tab";
import { Box, Grid, Skeleton, Stack, styled, Typography, useMediaQuery } from "~/common/mui";
import { ReduxFilterSection } from "~/features/search/components/Filters/ReduxFilterSection";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BallBearingIcon from "~/common/mui/icons/BallBearing";
import { PurchasePlaceProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { MoreInformationTab } from "~/features/productDetails/components/PdpTabs/MoreInformationTab";
import type { TabDefinition } from "~/features/search/hooks/useSearchPageTabs";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { Filter } from "~/features/search/models/filterConfigModels";
import { selectVisibleFilters } from "~/features/search/redux/filterConfigSelectors";
import { isComingFromSeriesTreeClick } from "~/features/search/redux/seriesTreeSelectors";
import { useSelectAppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import { PLPJsonLDComponent } from "~/features/search/components/PLPJsonLDComponent";
import {
  FULLBLEED_LEFT_SIZE,
  GRID_CONTAINER_FULLBLEED_STYLE,
} from "~/features/search/components/ProductResults/fullbleedUtils";
import { SearchDisplayMode } from "~/features/search/models/models";
import { useSRPResultsViewMode } from "~/features/search/components/ProductResults/useSRPResultsViewMode";
import { useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";
import { useFullbleed } from "~/features/search/components/ProductResults/useFullbleed";
import { PlpLinearConfigurator } from "~/domains/linear/component/PlpLinearConfigurator";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { SearchFiltersContainer } from "~/features/search/components/Filters/SearchFiltersContainer";
import { useScript } from "~/common/hooks/useScript";
import type { RecommendationPlacement } from "~/features/search/models/RecommendationPlacement";
import { setRecommendationPlacement } from "~/features/search/redux/actions";
import { useSession } from "~/app/config/useSession";

export { SearchFiltersContainer as FiltersContainer } from "~/features/search/components/Filters/SearchFiltersContainer";

const PlpTabs: Record<string, TabDefinition> = {
  Search: {
    id: "Products",
    gaName: "Product",
    icon: <BallBearingIcon sx={{ fontSize: 24 }} />,
    label: (context) =>
      context.t("page.plp.tab.search") + " (" + NF.getDefaultFormatter().format(context.productAmount) + ")",
    showFilters: true,
  },
  MoreInformation: {
    id: "More Information",
    gaName: "More Information",
    icon: <MenuBookIcon />,
    label: (context) => context.t("page.plp.tab.moreInfo"),
    showFilters: false,
  },
};

const trackTabActivation = (tab: TabDefinition) => {
  trackPlpTabActivation(tab.gaName!, window.SCHA.pageCategory);
};

interface Props {
  activeSalesCampaignCodes: string[];
  recommendationPlacement?: RecommendationPlacement;
  categoryName: string;
  bannerRelevantConfigurators?: Record<string, string>;
  pageAnchor?: string;
  showQuantityInCategoryName?: boolean;
}

export const ProductListPage = ({
  activeSalesCampaignCodes,
  recommendationPlacement,
  categoryName,
  bannerRelevantConfigurators,
  pageAnchor,
  showQuantityInCategoryName,
}: Props) => {
  const { t } = useTranslation();
  const isInSmartEdit = useIsSmartEdit();

  const isMinDesktopSizeM = useIsMinDesktopSizeM();

  const [activeTab, setActiveTab] = useTabHashLogic(PlpTabs, {
    defaultTab: PlpTabs.Search.id,
    tabContainerId: "tabsPLP",
    trackTabActivation,
    scrollOnPageLoad: true,
    pageAnchor,
  });

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setRecommendationPlacement(recommendationPlacement));
  }, [dispatch, recommendationPlacement]);

  useOnMount(() => {
    dispatch(searchResultsMounted());
  });

  useScript("https://orca-cdn.zoovu.com/core-js/zoovu-tag-library/zoovu-tag-library-latest.min.js");

  // Remove image header component slot html if it's empty
  useEffect(() => {
    const slot = document.getElementById("imageHeaderComponentSlot");
    if (slot) {
      const isEmpty = !slot.textContent?.trim();
      if (isEmpty) {
        slot.remove();
      }
    }
  }, []);

  // Takes care of handling button clicks on HeaderImageComponents in slot on the same page
  useEffect(() => {
    const clickHandler = (e: Event) => {
      e.preventDefault();
      trackPlpTabActivation("More Information", window.SCHA.pageCategory);
      setActiveTab(PlpTabs.MoreInformation.id, true, false);
    };
    const elements = document.querySelectorAll(".js-jsp__image-header__btn");
    elements.forEach((el) => el.addEventListener("click", clickHandler));
    return () => elements.forEach((el) => el.removeEventListener("click", clickHandler));
  }, [setActiveTab]);

  const { element: moreInfoContent } = useServerSideContent(`#moreInfoTab`);
  const isCMSContentVisible = hasAnyContent(moreInfoContent, ["#contentStream > *"]) || isInSmartEdit;

  const inFlight = useSelector(selectIsInFlight);

  const technicalInformation = useSelector(selectTechnicalInfo);
  const isMoreInformationVisibile = !!technicalInformation?.columns?.length;

  const productAmount = useSelector(selectProductResultAmount);
  const init = useSelector(selectIsInit);
  const appliedFilters = useSelectAppliedFilters();
  const isComingFromSeriesTree = useSelector(isComingFromSeriesTreeClick);
  let filterConfig = useSelector(selectVisibleFilters);
  if (isComingFromSeriesTree) {
    filterConfig = filterConfig.filter((item) => item.type !== Filter.TAXONOMY);
  }

  const [displayMode] = useSRPResultsViewMode();
  const enableTechnicalViewFullBleed = displayMode === SearchDisplayMode.TABULAR && activeTab === PlpTabs.Search.id;

  useEffect(() => {
    if (!init || inFlight) {
      return;
    }

    if (
      productAmount <= 0 &&
      activeTab === PlpTabs.MoreInformation.id &&
      !isMoreInformationVisibile &&
      !isCMSContentVisible
    ) {
      setActiveTab(PlpTabs.Search.id);
    }
  }, [productAmount, inFlight, init, setActiveTab]);

  const currentPage = useSelector(selectCurrentPage);
  const { component, position } =
    getSearchResultRecommendation(productAmount, currentPage, recommendationPlacement) ?? {};

  const tabVisibility: Record<string, boolean> = {
    [PlpTabs.Search.id]: true,
    [PlpTabs.MoreInformation.id]: isMoreInformationVisibile || isCMSContentVisible,
  };

  const tabComponent: Record<string, ReactNode> = {
    [PlpTabs.Search.id]: <ProductResults recommendationPosition={position} recommendationComponent={component} />,
    [PlpTabs.MoreInformation.id]: (
      <MoreInformationTab data={technicalInformation} cmsContent={moreInfoContent ?? undefined} />
    ),
  };

  const contentAmount = (technicalInformation?.columns ?? []).map((c) => c.items.length).reduce((a, b) => a + b, 0);

  const labelProps = {
    t,
    productAmount,
    contentAmount,
  };

  const getTabProps = (tab: TabDefinition) => ({
    value: tab.id,
    icon: tab.icon,
    label: tab.label(labelProps),
  });

  const tabs = [PlpTabs.Search, PlpTabs.MoreInformation].filter((tab) => tabVisibility[tab.id]);
  const showTabs = tabs.length > 1;

  let showFilters = tabs.find((tab) => tab.id === activeTab)?.showFilters ?? false;
  const noResults = init && productAmount === 0 && appliedFilters.length === 0 && filterConfig.length === 0;

  if (enableTechnicalViewFullBleed && !isMinDesktopSizeM) {
    showFilters = false;
  }

  let pageTitle;
  if (showQuantityInCategoryName && !inFlight) {
    pageTitle = `${categoryName} (${NF.getDefaultFormatter().format(productAmount)})`;
  } else {
    pageTitle = categoryName;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const bleed = useFullbleed(containerRef);
  const style = { [FULLBLEED_LEFT_SIZE]: `${bleed}px` } as CSSProperties;

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const transformedConfiguratorTools = Object.entries(bannerRelevantConfigurators ?? {}).map(([key, url], index) => {
    const lowerKey = key.toLowerCase();
    const translationKey = `plp.configurator.${lowerKey}`;

    return {
      id: index.toString(),
      label: t(translationKey),
      url,
    };
  });

  return (
    <>
      <PLPJsonLDComponent />
      <PurchasePlaceProvider value={"PLP"}>
        <ActiveSalesCampaignCodesProvider value={activeSalesCampaignCodes}>
          <SearchPageTypeProvider overrideType={SearchPageType.PLP}>
            <Stack spacing={{ phone: 2, "desktop-s": 3 }} ref={containerRef}>
              <StyledStack>
                {init && (
                  <>
                    <Typography variant="headlineSmall" component={"b"} sx={{ flex: 1 }}>
                      {pageTitle}
                    </Typography>

                    {isDesktop && transformedConfiguratorTools.length >= 1 && (
                      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                        <PlpLinearConfigurator config={transformedConfiguratorTools} />
                      </Box>
                    )}
                  </>
                )}
              </StyledStack>
              {!init && (
                <>
                  <Skeleton variant="rounded" height={40} width={"40%"} />
                  <Stack spacing={1} direction={"row"}>
                    <Skeleton variant="rounded" height={64} width={"100%"} sx={{ flex: 1 }} />
                    <Skeleton variant="rounded" height={64} width={"100%"} sx={{ flex: 1 }} />
                  </Stack>
                </>
              )}
              <ChildCategoryNavigation />
              {showTabs && (
                <Tabs
                  id={"tabsPLP"}
                  onChange={(e, val) => setActiveTab(val, true)}
                  value={activeTab}
                  fullWidthIndicator={false}
                  variant="fullWidth"
                >
                  {tabs.map((tab) => (
                    <Tab {...getTabProps(tab)} hidden={!tabVisibility[tab.id]} />
                  ))}
                </Tabs>
              )}
              <Grid
                container
                style={style}
                sx={{
                  ...(enableTechnicalViewFullBleed ? GRID_CONTAINER_FULLBLEED_STYLE : undefined),
                  alignItems: "flex-start",
                }}
              >
                <SearchFiltersContainer
                  sx={{ display: { phone: "none", "desktop-s": noResults ? "none" : "block" } }}
                  hidden={!showFilters}
                  size={{
                    "desktop-s": 3,
                  }}
                >
                  <ReduxFilterSection loading={!init} />
                </SearchFiltersContainer>

                {tabs.map((tab) => (
                  <Grid
                    key={tab.id}
                    hidden={activeTab !== tab.id}
                    size={{
                      phone: 12,
                      "desktop-s": tab.showFilters && !noResults ? 9 : 12,
                    }}
                  >
                    {tabComponent[tab.id]}
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </SearchPageTypeProvider>
          <RecentlyViewedProducts />
        </ActiveSalesCampaignCodesProvider>
      </PurchasePlaceProvider>
    </>
  );
};

export interface ProductListPageProps {
  categoryName?: string;
  bannerRelevantConfigurators?: Record<string, string>;
  pageAnchor?: string;
  showQuantityInCategoryName?: boolean;
  recommendationPlacement?: RecommendationPlacement;
}

export function createProductListPage({
  categoryName,
  bannerRelevantConfigurators,
  pageAnchor,
  showQuantityInCategoryName,
  recommendationPlacement,
}: ProductListPageProps = {}) {
  return (
    <SessionProductListPage
      categoryName={categoryName}
      bannerRelevantConfigurators={bannerRelevantConfigurators}
      pageAnchor={pageAnchor}
      showQuantityInCategoryName={showQuantityInCategoryName}
      recommendationPlacement={recommendationPlacement}
    />
  );
}

function SessionProductListPage({
  categoryName,
  bannerRelevantConfigurators,
  pageAnchor,
  showQuantityInCategoryName,
  recommendationPlacement,
}: ProductListPageProps) {
  const activeSalesCampaignCodes =
    useSession()?.salesCampaign?.activeSalesCampaigns.map((campaign) => campaign.code) ?? [];
  const finalCategoryName = categoryName ?? "";
  const finalConfigurators = bannerRelevantConfigurators;

  return (
    <ProductListPage
      activeSalesCampaignCodes={activeSalesCampaignCodes}
      recommendationPlacement={recommendationPlacement}
      categoryName={finalCategoryName}
      bannerRelevantConfigurators={finalConfigurators}
      pageAnchor={pageAnchor}
      showQuantityInCategoryName={showQuantityInCategoryName}
    />
  );
}

export const StyledStack = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
});
